import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useProspectSearchStore } from '../../stores/prospectSearchStore.js'

/**
 * Indicateur global :
 *  - Affiche un badge flottant pendant qu'une recherche tourne en arrière-plan.
 *  - Affiche un toast de notification quand le résultat arrive et que l'utilisateur
 *    n'est pas sur la page /prospects, avec un bouton pour y accéder.
 */
export default function GlobalSearchIndicator() {
  const loading = useProspectSearchStore((s) => s.loading)
  const results = useProspectSearchStore((s) => s.results)
  const notified = useProspectSearchStore((s) => s.notified)
  const currentQuery = useProspectSearchStore((s) => s.currentQuery)
  const markNotified = useProspectSearchStore((s) => s.markNotified)
  const location = useLocation()
  const navigate = useNavigate()

  const onProspectsPage = location.pathname.startsWith('/dashboard/prospection/b2b')

  // Notification quand le résultat arrive et que l'utilisateur est ailleurs
  useEffect(() => {
    if (!notified && results && !onProspectsPage) {
      toast.success(
        (t) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 600 }}>
                {results.total} prospect{results.total > 1 ? 's' : ''} trouvé{results.total > 1 ? 's' : ''}
              </div>
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                « {currentQuery} »
              </div>
            </div>
            <button
              onClick={() => {
                toast.dismiss(t.id)
                markNotified()
                navigate('/dashboard/prospection/b2b')
              }}
              style={{
                padding: '6px 12px', borderRadius: 6,
                background: '#5b5cf4', color: '#fff',
                fontWeight: 600, fontSize: 13, border: 'none', cursor: 'pointer',
              }}
            >
              Voir
            </button>
          </div>
        ),
        { duration: 8000, position: 'bottom-right' }
      )
      markNotified()
    }
  }, [notified, results, onProspectsPage, currentQuery, markNotified, navigate])

  // Marque comme vu dès qu'on arrive sur la page
  useEffect(() => {
    if (onProspectsPage && !notified && results) {
      markNotified()
    }
  }, [onProspectsPage, notified, results, markNotified])

  // Badge flottant pendant la recherche si pas sur la page prospects
  if (!loading || onProspectsPage) return null

  return (
    <div
      onClick={() => navigate('/dashboard/prospection/b2b')}
      style={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 999,
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 18px', borderRadius: 999,
        background: 'rgb(var(--rgb-surface) / 0.95)',
        border: '1px solid rgba(91, 92, 244, 0.3)',
        boxShadow: '0 8px 24px rgb(var(--rgb-shadow) / calc(0.4 * var(--shadow-k))), 0 0 20px rgba(91, 92, 244,0.15)',
        cursor: 'pointer',
        backdropFilter: 'blur(10px)',
      }}
    >
      <div
        style={{
          width: 16, height: 16, borderRadius: '50%',
          border: '2px solid rgba(91, 92, 244,0.2)', borderTop: '2px solid #5b5cf4',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <div style={{ fontSize: 13, color: 'rgb(var(--rgb-ink) / 0.9)' }}>
        <div style={{ fontWeight: 600 }}>Recherche en cours…</div>
        {currentQuery && (
          <div style={{ fontSize: 11, opacity: 0.5, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            « {currentQuery} »
          </div>
        )}
      </div>
    </div>
  )
}
